import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useAuth } from '../lib/auth/auth-context';
import { supabase as defaultClient } from '../lib/supabase';
import type { LynrxSupabaseClient } from '@lynrx/shared';
import { Alert } from '../components/ui/Alert';
import { DataTable, type Column } from '../components/ui/DataTable';
import { StatusBadge } from '../components/ui/StatusBadge';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';

// Mirrors public.route_status (lynrx-prod, May 14 2026). Surface the
// actual shipped enum here — the May 6 design used 'published' for what
// is now 'assigned'; we render the shipped name and let StatusBadge
// keep the 'published' alias as a safety net for any straggling code
// paths.
export const ROUTE_STATUS_VALUES = [
  'draft',
  'assigned',
  'in_progress',
  'completed',
  'cancelled',
] as const;

export type RouteStatusValue = (typeof ROUTE_STATUS_VALUES)[number];
export type RouteStatusFilter = 'all' | RouteStatusValue;

const STATUS_FILTER_VALUES: readonly RouteStatusFilter[] = [
  'all',
  ...ROUTE_STATUS_VALUES,
];

function isRouteStatusFilter(value: unknown): value is RouteStatusFilter {
  return (
    typeof value === 'string' &&
    (STATUS_FILTER_VALUES as readonly string[]).includes(value)
  );
}

// Snake_case → Title Case. Same helper shape as StatusBadge's internal
// formatter, but kept local because filter labels also need an "All"
// sentinel and an "In progress" label that StatusBadge already owns.
function statusLabel(value: RouteStatusFilter): string {
  if (value === 'all') return 'All';
  const words = value.split('_');
  const first = words[0];
  if (!first) return value;
  const head = first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  return [head, ...words.slice(1).map((w) => w.toLowerCase())].join(' ');
}

// Driver filter sentinel for "any". Anything else is expected to be a
// users.id UUID (the FK target of routes.driver_id, see
// 20260507160646_orders_stops_routes_tables.sql).
const DRIVER_ALL = 'all';
const DATE_ALL = 'all';

// Loose UUID shape. We don't enforce v4 specifically; any UUID-ish
// param is forwarded to the DB and filtered against driver_id.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Postgres date column shape (YYYY-MM-DD). Accepts the 'all' sentinel
// to widen the filter and any plausibly-shaped date string; if a user
// hand-edits the URL with a bad string, we fall back to today rather
// than throwing.
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function todayIso(): string {
  // Format today as local YYYY-MM-DD, matching how the Postgres date
  // column is interpreted by the UI. UTC midnight would tip the date
  // backward in negative offsets.
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type RouteSearch = {
  date: string; // ISO YYYY-MM-DD, or DATE_ALL
  status: RouteStatusFilter;
  driver: string; // UUID, or DRIVER_ALL
};

export const Route = createFileRoute('/_authenticated/routes/')({
  component: RoutesListPage,
  validateSearch: (search: Record<string, unknown>): RouteSearch => {
    const rawDate = search['date'];
    const date =
      typeof rawDate === 'string' && (rawDate === DATE_ALL || DATE_RE.test(rawDate))
        ? rawDate
        : todayIso();
    const rawStatus = search['status'];
    const status = isRouteStatusFilter(rawStatus) ? rawStatus : 'all';
    const rawDriver = search['driver'];
    const driver =
      typeof rawDriver === 'string' &&
      (rawDriver === DRIVER_ALL || UUID_RE.test(rawDriver))
        ? rawDriver
        : DRIVER_ALL;
    return { date, status, driver };
  },
});

const PAGE_SIZE = 50;

// Shape returned by the joined select below. The driver join is
// nullable because routes start as drafts before a driver is bound
// (Decision 17 + the routes table comment). Stops + orders are array
// joins; we accumulate counts client-side rather than asking Postgres
// to aggregate, which would require an RPC or a view.
type RouteListRow = {
  id: string;
  route_status: string;
  planned_for: string;
  driver_id: string | null;
  driver: { full_name: string | null; email: string } | null;
  stops: {
    id: string;
    orders: {
      id: string;
      status: string;
    }[];
  }[];
};

type DriverOption = {
  id: string;
  label: string;
};

type RouteCounts = {
  total: number;
  inProgress: number;
  completed: number;
  cancelled: number;
};

function formatRouteDate(iso: string): string {
  // routes.planned_for is YYYY-MM-DD. Parse as a local calendar date so
  // negative UTC offsets don't render the previous day. Same approach
  // as the route detail page's formatPlannedFor (PR #32).
  const parts = iso.split('-');
  if (parts.length === 3) {
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      const local = new Date(y, m - 1, d);
      return local.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    }
  }
  return iso;
}

// Label for the "Route" column. The schema doesn't carry a name today,
// so we synthesize: date + a short id prefix. Stable across renders
// and visually distinct enough that a dispatcher can tell two routes
// on the same day apart. Long-term, if/when a routes.name column is
// added, this is the one place to update.
function routeLabel(row: RouteListRow): string {
  return `${formatRouteDate(row.planned_for)} · ${row.id.slice(0, 8)}`;
}

function driverDisplay(row: RouteListRow): string {
  if (row.driver?.full_name) return row.driver.full_name;
  if (row.driver?.email) return row.driver.email;
  // route_status = 'draft' lives without a driver until publish; show
  // the unassigned signal explicitly rather than an em dash that could
  // also mean "data missing."
  return 'Unassigned';
}

function stopCount(row: RouteListRow): number {
  return row.stops.length;
}

function orderCount(row: RouteListRow): number {
  let n = 0;
  for (const stop of row.stops) n += stop.orders.length;
  return n;
}

function progressDisplay(row: RouteListRow): string {
  // Per the spec: only show progress while the route is in motion.
  // Once a route is completed/cancelled the cell stays empty — the
  // status badge already encodes the outcome.
  if (row.route_status !== 'in_progress') return '';
  const total = orderCount(row);
  if (total === 0) return '';
  let delivered = 0;
  for (const stop of row.stops) {
    for (const order of stop.orders) {
      if (order.status === 'delivered') delivered += 1;
    }
  }
  return `${delivered}/${total} delivered`;
}

function RoutesListPage(): JSX.Element {
  const { date, status, driver } = Route.useSearch();
  const navigate = useNavigate();
  const setSearch = useCallback(
    (next: Partial<RouteSearch>) => {
      void navigate({
        to: '/routes',
        search: { date, status, driver, ...next },
      });
    },
    [navigate, date, status, driver],
  );
  const goToRoute = useCallback(
    (routeId: string) => {
      void navigate({ to: '/routes/$routeId', params: { routeId } });
    },
    [navigate],
  );
  return (
    <RoutesListView
      date={date}
      status={status}
      driver={driver}
      onChange={setSearch}
      onRowOpen={goToRoute}
    />
  );
}

type ViewProps = {
  date: string;
  status: RouteStatusFilter;
  driver: string;
  onChange: (next: Partial<RouteSearch>) => void;
  onRowOpen: (routeId: string) => void;
  // Test seam — defaults to the singleton supabase client.
  client?: LynrxSupabaseClient;
};

export function RoutesListView({
  date,
  status,
  driver,
  onChange,
  onRowOpen,
  client = defaultClient,
}: ViewProps): JSX.Element {
  const { session, isReady } = useAuth();
  // Title only — the filter row + count strip carry the working
  // context; a subtitle would just label the page redundantly.
  useSetPageHeader({ title: 'Routes' });
  const [rows, setRows] = useState<RouteListRow[] | null>(null);
  const [drivers, setDrivers] = useState<DriverOption[] | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState<RouteCounts | null>(null);

  // Driver options load once per session — small table, rare to change
  // within a single session. RLS already scopes drivers to ones the
  // caller can see (drivers_select_self_or_tenant_or_pharmacy_or_admin
  // in 20260507180000_drivers.sql).
  useEffect(() => {
    if (!isReady || !session) return;
    let cancelled = false;
    void (async () => {
      const { data, error: err } = await client
        .from('drivers')
        .select('id, user:users!drivers_id_fkey(full_name, email)')
        .is('deleted_at', null);
      if (cancelled) return;
      if (err) {
        Sentry.captureException(err, { tags: { area: 'routes.list.fetch_drivers' } });
        // Don't fail the page if drivers fail to load — the driver
        // filter just stays at "All" with no other options.
        setDrivers([]);
        return;
      }
      const rawRows = (data ?? []) as unknown as {
        id: string;
        user: { full_name: string | null; email: string } | null;
      }[];
      const list = rawRows.map((r) => ({
        id: r.id,
        label: r.user?.full_name ?? r.user?.email ?? r.id.slice(0, 8),
      }));
      // Stable alphabetical order so the dropdown doesn't shuffle.
      list.sort((a, b) => a.label.localeCompare(b.label));
      setDrivers(list);
    })();
    return () => {
      cancelled = true;
    };
  }, [client, isReady, session]);

  const fetchPage = useCallback(
    async (nextPage: number, filters: RouteSearch) => {
      if (!session) return;
      setLoading(true);
      setError(null);
      const from = nextPage * PAGE_SIZE;
      const to = from + PAGE_SIZE; // peek one past the page for hasMore

      let query = client
        .from('routes')
        .select(
          'id, route_status, planned_for, driver_id, driver:users!routes_driver_id_fkey(full_name, email), stops(id, orders!orders_dropoff_stop_id_fkey(id, status))',
        )
        .is('deleted_at', null)
        .order('planned_for', { ascending: false })
        .order('created_at', { ascending: false });

      if (filters.date !== DATE_ALL) {
        query = query.eq('planned_for', filters.date);
      }
      if (filters.status !== 'all') {
        query = query.eq('route_status', filters.status);
      }
      if (filters.driver !== DRIVER_ALL) {
        query = query.eq('driver_id', filters.driver);
      }

      const { data, error: err } = await query.range(from, to);
      setLoading(false);
      if (err) {
        Sentry.captureException(err, { tags: { area: 'routes.list.fetch' } });
        setError('Could not load routes.');
        return;
      }
      const all = (data ?? []) as unknown as RouteListRow[];
      const pageRows = all.slice(0, PAGE_SIZE);
      setRows(pageRows);
      setHasMore(all.length > PAGE_SIZE);
      setPage(nextPage);
    },
    [client, session],
  );

  // Per-status counts — shares the date + driver filters with the
  // page query but ignores the status filter so the strip always
  // shows the shape of the whole working window.
  const fetchCounts = useCallback(
    async (filters: Pick<RouteSearch, 'date' | 'driver'>) => {
      if (!session) return;
      const base = () => {
        let q = client
          .from('routes')
          .select('id', { count: 'exact', head: true })
          .is('deleted_at', null);
        if (filters.date !== DATE_ALL) q = q.eq('planned_for', filters.date);
        if (filters.driver !== DRIVER_ALL) q = q.eq('driver_id', filters.driver);
        return q;
      };
      const [totalRes, inProgressRes, completedRes, cancelledRes] = await Promise.all([
        base(),
        base().eq('route_status', 'in_progress'),
        base().eq('route_status', 'completed'),
        base().eq('route_status', 'cancelled'),
      ]);
      if (totalRes.error || inProgressRes.error || completedRes.error || cancelledRes.error) {
        Sentry.captureException(
          totalRes.error ?? inProgressRes.error ?? completedRes.error ?? cancelledRes.error,
          { tags: { area: 'routes.list.fetch_counts' } },
        );
        return;
      }
      setCounts({
        total: totalRes.count ?? 0,
        inProgress: inProgressRes.count ?? 0,
        completed: completedRes.count ?? 0,
        cancelled: cancelledRes.count ?? 0,
      });
    },
    [client, session],
  );

  // Reset to page 0 whenever any filter changes — old offset is
  // meaningless against a new filtered set.
  useEffect(() => {
    if (!isReady || !session) return;
    void fetchPage(0, { date, status, driver });
    void fetchCounts({ date, driver });
  }, [isReady, session, fetchPage, fetchCounts, date, status, driver]);

  const columns: Column<RouteListRow>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Route',
        render: (r) => (
          <Link
            to="/routes/$routeId"
            params={{ routeId: r.id }}
            className="text-primary hover:underline"
          >
            {routeLabel(r)}
          </Link>
        ),
      },
      {
        key: 'date',
        header: 'Date',
        render: (r) => formatRouteDate(r.planned_for),
      },
      {
        key: 'driver',
        header: 'Driver',
        render: driverDisplay,
      },
      {
        key: 'stops',
        header: 'Stops',
        render: (r) => String(stopCount(r)),
      },
      {
        key: 'orders',
        header: 'Orders',
        render: (r) => String(orderCount(r)),
      },
      {
        key: 'status',
        header: 'Status',
        render: (r) => <StatusBadge entity="route" status={r.route_status} />,
      },
      {
        key: 'progress',
        header: 'Progress',
        render: (r) => progressDisplay(r) || '—',
      },
    ],
    [],
  );

  const emptyMessage =
    rows === null
      ? 'Loading routes…'
      : date === DATE_ALL
        ? 'No routes match the current filters.'
        : `No routes for ${formatRouteDate(date)}. Routes appear here once dispatchers build them from pending orders.`;

  return (
    <>
      {/* Filter row — Date / Status / Driver, compact uppercase labels. */}
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <FilterField label="Date">
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={date === DATE_ALL ? '' : date}
              onChange={(e) => {
                const next = e.target.value;
                onChange({ date: next === '' ? DATE_ALL : next });
              }}
              aria-label="Filter routes by date"
              className="rounded-md border border-border bg-bg px-2.5 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {date !== DATE_ALL ? (
              <button
                type="button"
                onClick={() => onChange({ date: DATE_ALL })}
                className="text-xs font-medium text-primary hover:underline"
              >
                All dates
              </button>
            ) : null}
          </div>
        </FilterField>

        <FilterField label="Status">
          <select
            value={status}
            onChange={(e) => {
              const next = e.target.value;
              if (isRouteStatusFilter(next)) onChange({ status: next });
            }}
            className="rounded-md border border-border bg-bg px-2.5 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Filter routes by status"
          >
            {STATUS_FILTER_VALUES.map((s) => (
              <option key={s} value={s}>
                {statusLabel(s)}
              </option>
            ))}
          </select>
        </FilterField>

        <FilterField label="Driver">
          <select
            value={driver}
            onChange={(e) => onChange({ driver: e.target.value })}
            className="rounded-md border border-border bg-bg px-2.5 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Filter routes by driver"
            disabled={drivers === null}
          >
            <option value={DRIVER_ALL}>All drivers</option>
            {(drivers ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        </FilterField>
      </div>

      <CountSummary counts={counts} />

      {error ? (
        <div className="mb-6">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

        <DataTable
          columns={columns}
          rows={rows ?? []}
          rowKey={(r) => r.id}
          emptyMessage={emptyMessage}
          onRowClick={(r) => onRowOpen(r.id)}
        />

        <div className="mt-4 flex items-center justify-between text-sm text-ink-2">
          <span>
            {rows && rows.length > 0
              ? `Page ${page + 1} · showing ${rows.length} route${rows.length === 1 ? '' : 's'}`
              : ''}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                if (page > 0) void fetchPage(page - 1, { date, status, driver });
              }}
              disabled={loading || page === 0}
              className="rounded-md border border-border bg-bg px-3 py-1 text-sm text-ink hover:bg-bg-soft disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => {
                if (hasMore) void fetchPage(page + 1, { date, status, driver });
              }}
              disabled={loading || !hasMore}
              className="rounded-md border border-border bg-bg px-3 py-1 text-sm text-ink hover:bg-bg-soft disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
    </>
  );
}

// Small uppercase-label wrapper for each filter control. Same pattern
// as the orders page's FilterField — kept local to avoid a shared
// component that would tempt callers to grow it beyond its purpose.
function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-medium uppercase tracking-wide text-ink-2">
        {label}
      </span>
      {children}
    </label>
  );
}

// Working-set summary strip — total / in progress / completed /
// cancelled with the signal palette. Mirrors the orders page strip;
// kept local for the same reason as FilterField.
function CountSummary({ counts }: { counts: RouteCounts | null }): JSX.Element | null {
  if (!counts) return null;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]">
      <span className="text-ink-2">
        <span className="font-semibold text-ink">{counts.total}</span> total
      </span>
      <span className="text-ink-2">
        <span className="font-semibold text-signal-blue">{counts.inProgress}</span> in progress
      </span>
      <span className="text-ink-2">
        <span className="font-semibold text-signal-green">{counts.completed}</span> completed
      </span>
      <span className="text-ink-2">
        <span
          className={
            counts.cancelled > 0
              ? 'font-semibold text-signal-red'
              : 'font-semibold text-ink-2'
          }
        >
          {counts.cancelled}
        </span>{' '}
        cancelled
      </span>
    </div>
  );
}
